import React from 'react';
import { Box, Typography, CircularProgress } from '@mui/material';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { MerchantsTab } from './MerchantsTab';
import { PaymentIssuesCard } from './PaymentIssuesCard';
import './OverviewTab.css';

/**
 * ISO Admin Overview Tab Component
 * Displays ISO-specific metrics with merchant breakdown for ISO Admins
 */
export function FIAdminOverviewTab({ fiSummary, merchantMetrics, paymentIssues, loading, fiId, fiName, onNavigateToTab }) {
  function formatCurrency(value) {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
    }).format(value || 0);
  }

  function formatNumber(value) {
    return new Intl.NumberFormat('en-US').format(value || 0);
  }

  function handleMetricClick(tab) {
    if (onNavigateToTab) {
      onNavigateToTab(tab);
    }
  }

  // Compact Metric Card Component
  function CompactMetricCard({ icon, label, value, subtitle, color = 'primary', clickable, onClick }) {
    return (
      <div
        className={`metric-card-compact ${color} ${clickable ? 'clickable' : ''}`}
        onClick={clickable ? onClick : undefined}
      >
        <div className="metric-icon">
          <FontAwesomeIcon icon={icon} />
        </div>
        <div className="metric-label">{label}</div>
        <div className="metric-value">{value}</div>
        {subtitle && <div className="metric-subtitle">{subtitle}</div>}
      </div>
    );
  }

  // Value Chip Component for gain/loss display
  function ValueChip({ value, showSign = true }) {
    const isPositive = value >= 0;
    const chipClass = isPositive ? 'positive' : 'negative';
    const displayValue = formatCurrency(Math.abs(value));
    const sign = showSign ? (isPositive ? '+' : '-') : '';

    return (
      <span className={`status-chip ${chipClass}`}>
        {sign}{displayValue.replace('$', '').replace('-', '')}
      </span>
    );
  }

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" py={4}>
        <CircularProgress size={32} />
      </Box>
    );
  }

  if (!fiSummary) {
    return (
      <Box p={3}>
        <Typography color="text.secondary">No data available</Typography>
      </Box>
    );
  }

  // Calculate values for display
  const totalTransactions = parseInt(fiSummary.total_transactions || 0, 10);
  const totalVolume = parseFloat(fiSummary.total_volume_usd || 0);
  const totalFeesCollected = parseFloat(fiSummary.total_fees_collected || 0);
  const totalCommission = parseFloat(fiSummary.total_commission || fiSummary.total_commission_earned || 0);
  const activeMerchants = merchantMetrics?.length || 0;
  const avgTransactionValue = totalTransactions > 0 ? totalVolume / totalTransactions : 0;

  return (
    <Box>
      <Typography variant="h5" gutterBottom sx={{ mb: 3 }}>
        {fiName ? `${fiName} Overview` : 'ISO Overview'}
      </Typography>

      {/* FI Activity Section */}
      <div className="metrics-section">
        <div className="metrics-section-title">Activity Summary</div>
        <div className="metrics-grid">
          <CompactMetricCard
            icon={['fal', 'receipt']}
            label="Total Transactions"
            value={formatNumber(totalTransactions)}
            subtitle="In selected period"
            color="primary"
            clickable
            onClick={() => handleMetricClick('transactions')}
          />
          <CompactMetricCard
            icon={['fal', 'dollar-sign']}
            label="Total Volume"
            value={formatCurrency(totalVolume)}
            subtitle="USD processed"
            color="success"
            clickable
            onClick={() => handleMetricClick('transactions')}
          />
          <CompactMetricCard
            icon={['fal', 'store']}
            label="Active Merchants"
            value={formatNumber(activeMerchants)}
            subtitle="With transactions"
            color="info"
          />
          <CompactMetricCard
            icon={['fal', 'chart-line']}
            label="Avg Transaction"
            value={formatCurrency(avgTransactionValue)}
            subtitle="Per transaction"
            color="primary"
          />
        </div>
      </div>

      {/* Payment Issues Alert Card */}
      <PaymentIssuesCard
        paymentIssues={paymentIssues}
        onNavigateToTab={onNavigateToTab}
      />

      {/* Revenue & Commissions Section */}
      <div className="metrics-section">
        <div className="metrics-section-title">Revenue & Commissions</div>
        <div className="metrics-grid">
          <CompactMetricCard
            icon={['fal', 'hand-holding-usd']}
            label="Fees Collected"
            value={formatCurrency(totalFeesCollected)}
            subtitle="Total from merchants"
            color="success"
          />
          <CompactMetricCard
            icon={['fal', 'coins']}
            label="Commission Earned"
            value={<ValueChip value={totalCommission} showSign={false} />}
            subtitle="Your share"
            color="success"
          />
          <CompactMetricCard
            icon={['fal', 'percentage']}
            label="Effective Rate"
            value={totalVolume > 0 ? `${((totalFeesCollected / totalVolume) * 100).toFixed(2)}%` : '0.00%'}
            subtitle="Fees / Volume"
            color="info"
          />
          <CompactMetricCard
            icon={['fal', 'chart-pie']}
            label="Commission Rate"
            value={totalFeesCollected > 0 ? `${((totalCommission / totalFeesCollected) * 100).toFixed(1)}%` : '0.0%'}
            subtitle="Of fees collected"
            color="info"
          />
        </div>
      </div>

      {/* Merchants Section */}
      <Box mt={4}>
        <MerchantsTab merchantMetrics={merchantMetrics || []} fiId={fiId} />
      </Box>
    </Box>
  );
}
